/**
 * Design Tokens - Enterprise grade.
 *
 * Nguyên tắc:
 *  - Neutral palette chính, accent rất tinh tế.
 *  - Radius tối đa 6px (không 12-16).
 *  - Shadow gần như không có; chỉ dùng cho overlay/modal/dropdown.
 *  - Spacing scale rõ ràng, đồng bộ toàn hệ thống.
 *  - Typography hierarchy bằng size + weight, không màu mè.
 *
 * ĐÂY LÀ NGUỒN MÀU DUY NHẤT của CMS. Ba nơi tiêu thụ:
 *  1. theme/dashboardTheme.js  -> ConfigProvider của antd
 *  2. theme/variables.css      -> biến :root cho file .css/.scss
 *  3. tailwind.config.js       -> lớp text-brand-* / bg-surface-*
 * Thiếu màu thì bổ sung ở đây, không viết hex thẳng trong component.
 */

// ============ NEUTRAL PALETTE (slate) ============
export const neutral = {
  0: "#FFFFFF",
  25: "#FCFCFD",
  50: "#F8FAFC",
  100: "#F1F5F9",
  200: "#E2E8F0",
  300: "#CBD5E1",
  400: "#94A3B8",
  500: "#64748B",
  600: "#475569",
  700: "#334155",
  800: "#1E293B",
  900: "#0F172A",
};

// ============ BRAND ============
// Bộ nhận diện dùng chung với mini app UBND phường/xã. Cán bộ dùng cả hai hệ trên
// cùng một nghiệp vụ nên hai màu khác nhau là hai sản phẩm trong mắt họ.
export const brand = {
  primary: "#0070D7",       // Nút chính, link, tab đang chọn      (tương phản 4,88)
  primaryHover: "#005AAC",  // Hover / pressed, chữ trên nền sáng  (6,87)
  primaryActive: "#005AAC",
  bright: "#1A7EDB",        // Nền nhấn, icon — KHÔNG dùng làm chữ (4,16)
  brightText: "#1874C9",    // Bản đậm hơn để làm chữ              (4,79)
  sky: "#68D4FD",           // Chỉ gradient / nền, không bao giờ làm chữ
  // Thanh điều hướng để tông TRẦM, màu thương hiệu chỉ làm điểm nhấn.
  // Sơn cả sidebar bằng #0070D7 thì màn quá rực — đây là phần mềm thu tiền
  // cho cán bộ phường và ban quản lý bất động sản, không phải app tiêu dùng.
  navBg: "#0F2A47",         // Nền sidebar + thanh trên  (chữ trắng 14,56 · bão hoà 65%)
  navSub: "#223B56",        // Nền menu con, sáng hơn một nấc kiểu khối lồng (lệch 1,27)
  navActive: "#0070D7",     // Mục đang chọn = ĐÚNG màu thương hiệu, nổi 2,98 trên nền
  primarySubtle: "#F0F6FD", // Nền khối nhấn nhẹ
  borderTint: "#B8D7F4",    // Viền khối nhấn

  // Giữ tên cũ cho 42 file đang dùng brand.accent — trỏ về cùng màu thương hiệu
  accent: "#0070D7",
};

// ============ SURFACE / NỀN ============
export const surface = {
  page: "#F5F7FA",     // Nền trang
  card: "#FFFFFF",     // Nền thẻ
  input: "#F8FAFC",    // Nền ô nhập
  divider: "#E8EDF2",  // Đường kẻ
  onDark: "#FFFFFF",        // Chữ trên nền tối của thanh điều hướng
  onDarkMuted: "rgba(255, 255, 255, 0.65)", // Chữ phụ trên nền tối
};

// ============ SEMANTIC ============
// Mỗi trạng thái có HAI biến thể:
//  - `x`       : dùng làm CHỮ và SỐ, đã hiệu chỉnh đạt WCAG AA (>= 4.5 trên nền trắng)
//  - `xSolid`  : đúng tông thương hiệu, dùng làm NỀN / ICON / thanh tiến độ / đường biểu đồ
// Lý do tách: mini app chữ to nền màu, còn CMS là bảng biểu chữ 13px. Dùng chung
// một mã thì #FF9D2E chỉ đạt 2,08 — đó là chữ mờ, không phải chữ màu.
export const semantic = {
  success: "#128555",
  successSolid: "#16A66A",
  successSubtle: "#EAF9F2",
  successBorder: "#B8E8D0",

  warning: "#A8681E",
  warningSolid: "#FF9D2E",
  warningSubtle: "#FFF5E8",
  warningBorder: "#FFE0B8",

  danger: "#E5065C",
  dangerSolid: "#FF0766",
  dangerSubtle: "#FFF0F4",
  dangerBorder: "#FFB8C9",

  info: "#1874C9",
  infoSolid: "#1A7EDB",
  infoSubtle: "#F0F6FD",
};

// ============ PHỤ TRỢ ============
// Tím KHÔNG phải màu thương hiệu thứ hai — chỉ để phân biệt loại khoản thu đặc biệt.
export const accentAlt = {
  purple: "#656DC7",       // Làm chữ (4,60)
  purpleSolid: "#757FE7",  // Làm nền / icon
  purpleSubtle: "#F0F1FF",
};

// ============ GRADIENT THƯƠNG HIỆU ============
// Chỉ dùng ở màn đăng nhập, banner toàn hệ thống và khối xem trước mini app.
// Không dùng cho thẻ số liệu và nút — đó đúng là lỗi màn Tổng quan vừa sửa.
export const gradient = {
  brand: `linear-gradient(135deg, ${"#005AAC"} 0%, ${"#0070D7"} 55%, ${"#1A7EDB"} 100%)`,
};

// ============ RADIUS ============
export const radius = {
  none: 0,
  sm: 2,
  base: 4,
  md: 6,
  lg: 6,       // Cap ở 6px - không lớn hơn
  pill: 999,
};

// ============ SPACING SCALE ============
export const spacing = {
  0: 0,
  1: 4,
  2: 8,
  3: 12,
  4: 16,
  5: 20,
  6: 24,
  8: 32,
  10: 40,
  12: 48,
  16: 64,
};

// ============ TYPOGRAPHY ============
export const typography = {
  family:
    "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  // Sizes
  size: {
    xs: 11,
    sm: 12,
    base: 13,
    md: 14,
    lg: 16,
    xl: 18,
    "2xl": 22,
    "3xl": 28,
  },
  weight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  // Line height
  leading: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.65,
  },
};

// ============ SHADOWS (rất nhẹ) ============
export const shadow = {
  none: "none",
  // Subtle: card / panel
  xs: "0 1px 2px rgba(15, 23, 42, 0.04)",
  // Dropdown / popover
  sm: "0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)",
  // Modal / dialog
  md: "0 4px 12px rgba(15, 23, 42, 0.08), 0 2px 4px rgba(15, 23, 42, 0.04)",
  // Hover lift
  hover: "0 2px 6px rgba(15, 23, 42, 0.06)",
};

// ============ BORDERS ============
export const border = {
  hairline: `1px solid ${neutral[200]}`,
  base: `1px solid ${neutral[300]}`,
  strong: `1px solid ${neutral[400]}`,
  focus: `1px solid ${brand.accent}`,
  divider: `1px solid ${neutral[100]}`,
};

// ============ LAYOUT ============
export const layout = {
  pagePadding: spacing[6],     // 24
  sectionGap: spacing[6],      // 24
  cardPadding: spacing[6],     // 24
  fieldGap: spacing[4],        // 16
  contentMaxWidth: 1440,
};

// ============ Z-INDEX ============
export const z = {
  dropdown: 1000,
  sticky: 1100,
  fixed: 1200,
  modal: 1300,
  popover: 1400,
  tooltip: 1500,
};

const tokens = {
  neutral,
  brand,
  surface,
  semantic,
  accentAlt,
  gradient,
  radius,
  spacing,
  typography,
  shadow,
  border,
  layout,
  z,
};

export default tokens;
